import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import CollectionCard from "@/components/CollectionCard";
import { Loader2, ArrowLeft, Images, LayoutGrid } from "lucide-react";

export default function CuratorProfile() {
  const { ownerId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/public/curators/${ownerId}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [ownerId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-parchment">
        <Header />
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-brick" /></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-parchment">
        <Header />
        <div className="text-center py-24">
          <p className="font-serif text-2xl text-ink">Curator not found</p>
          <Link to="/" className="text-brick font-semibold hover:underline mt-3 inline-block">← Back home</Link>
        </div>
      </div>
    );
  }

  const { curator, collections } = data;

  return (
    <div className="min-h-screen bg-parchment">
      <Header />

      <div className="border-b border-stoneline bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-brick mb-6">
            <ArrowLeft className="h-4 w-4" /> All collections
          </Link>
          <div className="flex items-center gap-5" data-testid="curator-header">
            <img
              src={curator.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(curator.name)}&size=128`}
              alt={curator.name}
              className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover border-4 border-gold shadow-sm"
            />
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-brick font-semibold">Curator</p>
              <h1 className="text-3xl sm:text-4xl font-serif font-bold text-ink mt-1">{curator.name}</h1>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><LayoutGrid className="h-4 w-4" /> {curator.collection_count} collection{curator.collection_count === 1 ? "" : "s"}</span>
                <span className="inline-flex items-center gap-1.5"><Images className="h-4 w-4" /> {curator.image_count} images</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {collections.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-stoneline rounded-xl bg-white/50 text-muted-foreground">
            No published collections yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {collections.map((c) => (
              <CollectionCard key={c.id} collection={c} to={`/c/${c.id}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
